import { Component, Input, Output, EventEmitter, OnInit, OnChanges, OnDestroy, SimpleChanges, ChangeDetectorRef, ViewChild, TemplateRef, NgZone } from '@angular/core';
import { NbDialogService } from '@nebular/theme';
import { CampaignService, ScheduledCampaign, SegmentMetadata, SegmentMetadataItem } from 'src/app/deebr/service/campaign.service';
import { CategoryService } from 'src/app/deebr/service/category.service';
import { DicoService } from 'src/app/deebr/service/dico.service';
import { FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { CampaignSegment } from 'src/app/deebr/model/campaign-segment.model';
import { Category } from 'src/app/deebr/model/category.model';
import { DicoEntry } from 'src/app/deebr/model/dico-entry.model';
import { MetaCategory } from 'src/app/deebr/model/meta-category.model';
import { of, Observable, Subscription, Subject } from 'rxjs'; // Import needed rxjs
import { debounceTime, takeUntil } from 'rxjs/operators';

import { Options } from '@angular-slider/ngx-slider';
import { ChartConfiguration, ChartData, ChartOptions } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import * as d3 from 'd3';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const BRAND = {
    red: '#ff6384',
    blue: '#35a2eb',
    yellow: '#ffd877',
    green: '#74cdcd',
    grey: '#f1f2f4',
    grey2: '#c1d6e1',
} as const;

const DEFAULT_ACHAT_COLORS: Record<string, string> = {
    'Prospects': BRAND.red,
    'Perdu': '#b0bec5',
    'Inactifs': BRAND.blue,
    'Occasionnels': BRAND.yellow,
    'Réguliers': BRAND.grey,
    'Fidèles': BRAND.green,
    'VIP': BRAND.grey2,
};

const DEFAULT_EMAIL_COLORS: Record<string, string> = {
    'Aucune intéraction': BRAND.grey,
    'Perdu': '#b0bec5',
    'Inactifs': BRAND.red,
    'Occasionnels': BRAND.blue,
    'Réguliers': BRAND.yellow,
    'Fidèles': BRAND.green,
    'Ambassadeurs': '#4fc3f7',
};

function colorsFor(labels: string[], map: Record<string, string>, fallback: Record<string, string> = {}) {
    return labels.map(l => {
        const key = (l ?? '').normalize('NFC').trim();
        return map[key] ?? fallback[key] ?? BRAND.grey;
    });
}


interface AudienceGroup {
    id: number;
    segments: CampaignSegment[];
    // UI State for this group's input
    searchControl: FormControl;
    metaControl: FormControl;
    filteredCategories$: Observable<Category[]>;
    showInput?: boolean;
}

@Component({
    selector: 'app-campaign-sidebar',
    templateUrl: './campaign-sidebar.component.html',
    styleUrls: ['./campaign-sidebar.component.scss'],
    standalone: false
})
export class CampaignSidebarComponent implements OnInit, OnChanges, OnDestroy {
    @Input() date: Date = new Date();
    @Input() campaignInput: ScheduledCampaign | null = null;
    @Output() closed = new EventEmitter<void>();
    @Output() saved = new EventEmitter<ScheduledCampaign>();

    @ViewChild('globalChart') globalChart?: BaseChartDirective;

    campaign: ScheduledCampaign | null = null;
    loading = false;
    isGlobalLoading = false;
    chartPlugins = [ChartDataLabels];

    @ViewChild('dialogCurveError') dialogCurveError: TemplateRef<any> | undefined;
    @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
    hasCurveError = false;
    errorMessage = '';

    // Charts Properties
    pieChartDataSegmentAchat: ChartData<'pie'> = { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
    pieChartOptionsSegmentAchat: ChartOptions<'pie'> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 }, padding: 6 } } } };

    pieChartDataSegmentEmail: ChartData<'pie'> = { labels: [], datasets: [{ data: [], backgroundColor: [] }] };
    pieChartOptionsSegmentEmail: ChartOptions<'pie'> = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 }, padding: 6 } } } };

    agePyramidData: ChartData<'bar'> = { labels: [], datasets: [] };
    agePyramidOptions: ChartOptions<'bar'> = {
        indexAxis: 'y',
        responsive: true,
        scales: {
            x: { stacked: true },
            y: { stacked: true }
        },
        plugins: {
            legend: { display: true },
            datalabels: { display: false }
        }
    };

    genreRealiteChartData: ChartData<'bar'> = { labels: [], datasets: [] };
    genreSegmentChartData: ChartData<'bar'> = { labels: [], datasets: [] };
    genreSegmentChartOptions: ChartOptions<'bar'> = {
        indexAxis: 'y',
        responsive: true,
        scales: {
            x: { max: 100 },
            y: { grid: { display: false } }
        },
        layout: { padding: { right: 50 } },
        plugins: {
            legend: { display: false },
            datalabels: {
                anchor: 'end',
                align: 'end',
                formatter: (val: number) => (Math.round(val * 10) / 10).toFixed(1) + '%'
            }
        }
    };

    // Map
    regionsMapData: any[] = [];
    regionsMapSvg: any;
    regionsMapContainer: any;
    regionsMapTooltip: any;
    regionsGeoData: any;
    regionsMapWidth = 600;
    regionsMapHeight = 500;
    regionsMapColorScale: any;

    // Reused from CampaignComponent
    form!: FormGroup;

    // Groups Data
    audienceGroups: AudienceGroup[] = [];

    // Shared Data
    categories: Category[] = [];
    planningCategoriesMap: { [metaId: number]: Category[] } = {};
    allCategories: Category[] = [];
    percentage: number = 30; // Default for new segments

    metaCategories!: MetaCategory[];
    // metaCategory!: MetaCategory; // Deprecated, per-group now
    // filteredCategories$!: Observable<Category[]>; // Deprecated, per-group now
    // currentCategory: Category | undefined; // Deprecated
    // searchString = ''; // Deprecated

    // Slider options
    sliderOptions: Options = {
        floor: 0,
        ceil: 100,
        step: 1,
        showSelectionBar: true
    };


    // Stats properties
    totalMails = 0;
    currentCurve: { x: number; y: number }[] | null = null;
    currentStats: {
        numb_mail: number;
        pourcentBase: number;
        potential: number;
        lift: number;
    } | null = null;

    globalStats: {
        numb_mail: number;
        pourcentBase: number;
        potential: number;
        lift: number;
        percentage?: number;
    } | null = null;
    globalPercentage: number = 30; // default
    globalPressureActive: boolean = false; // Flag to track if user explicitly used slider
    backendOptimal: number = 0; // Backend-supplied optimal, always shown as fixed dot on curve
    hasUnsavedChanges = false;
    hasAudiences = false; // true when at least one category is selected
    private savedPressure: number | null = null;
    existingCapping: any = null; // Preserved from optimize-dialog, re-injected on save
    storedRawCurve: { x: number, y: number }[] | null = null;
    renderedCurve: { x: number, y: number }[] | null = null;
    globalScopeCount: number = 0;

    // Segment metadata from BigQuery
    segmentMetadata: SegmentMetadata = {};
    achatColors: Record<string, string> = { ...DEFAULT_ACHAT_COLORS };
    emailColors: Record<string, string> = { ...DEFAULT_EMAIL_COLORS };
    // Lookup maps for tooltips: segment name → metadata item
    achatMetadataMap: Record<string, SegmentMetadataItem> = {};
    emailMetadataMap: Record<string, SegmentMetadataItem> = {};
    valeurMetadataMap: Record<string, SegmentMetadataItem> = {};

    // Filtre global canal d'achat : 'all' | 'web' | 'magasin' | 'mixte'
    selectedChannel: string = 'all';

    /** Build external HTML tooltip options for a pie chart */
    private buildPieTooltipOptions(getMetadataMap: () => Record<string, SegmentMetadataItem>): any {
        return {
            enabled: false,
            external: (ctx: any) => {
                let el = document.getElementById('segment-chart-tooltip');
                if (!el) {
                    el = document.createElement('div');
                    el.id = 'segment-chart-tooltip';
                    el.style.cssText = 'position:fixed;pointer-events:none;z-index:99999;background:rgba(30,30,30,0.95);color:#fff;border-radius:6px;padding:10px 14px;font-size:12px;line-height:1.5;max-width:350px;white-space:normal;word-wrap:break-word;box-shadow:0 2px 8px rgba(0,0,0,0.3);transition:opacity 0.15s;';
                    document.body.appendChild(el);
                }

                const tooltip = ctx.tooltip;
                if (tooltip.opacity === 0) {
                    el.style.opacity = '0';
                    return;
                }

                const label = (tooltip.title?.[0] ?? '').normalize('NFC').trim();
                const value = tooltip.body?.[0]?.lines?.[0] ?? '';
                const meta = getMetadataMap()[label];

                let html = `<strong>${label}</strong>`;
                if (value) html += `<br>${value}`;
                if (meta) {
                    if (meta.description) html += `<br><span style="color:#ccc">${meta.description}</span>`;
                    if (meta.criteria) html += `<br><span style="color:#aaa">Critère: ${meta.criteria}</span>`;
                }
                el.innerHTML = html;
                el.style.opacity = '1';
                el.style.left = tooltip.caretX + ctx.chart.canvas.getBoundingClientRect().left + 'px';
                el.style.top = tooltip.caretY + ctx.chart.canvas.getBoundingClientRect().top + 'px';
            }
        };
    }


    globalCurveData: ChartData<'line'> | null = null;
    globalCurveOptions: ChartConfiguration['options'] = {
        animation: false,
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (context) => `Lift: ${(context.parsed as any).y}`
                }
            }
        },
        scales: {
            x: {
                type: 'linear',
                min: 1,
                max: 100,
                title: { display: true, text: '% Base Totale' }
            },
            y: {
                title: { display: true, text: 'Lift' }
            }
        }
    };

    globalSliderOptions: Options = {
        floor: 0,
        ceil: 100,
        step: 1,
        showSelectionBar: true
    };

    // Filters
    filters: any[] = [];
    excludeRecentBuyers = false;
    dateColumn: string | null = null;

    // AI name suggestion
    isSuggestingName = false;

    // Debounce Subject
    private destroy$ = new Subject<void>();
    private calcStatsSubject = new Subject<{ fromSlider: boolean, userModified: boolean }>();

    // Active preview request — unsubscribed to prevent race conditions
    private globalPreviewSubscription?: Subscription;

    // Column Definitions
    columnDefs: Record<string, { type: 'string' | 'number' | 'date'; label: string; options?: string[] }> = {};

    availableColumns: string[] = [];
    operators = ['=', '!=', '>', '<', '>=', '<=', 'LIKE', 'IN'];

    // Autocomplete helpers
    getFilteredOptions(filter: any): Observable<string[]> {
        const def = this.columnDefs[filter.column];
        if (!def || !def.options) return of([]);
        return of(def.options);
    }

    // Check type
    isStringColumn(column: string): boolean {
        return this.columnDefs[column]?.type === 'string';
    }

    isDateColumn(column: string): boolean {
        return this.columnDefs[column]?.type === 'date';
    }

    getSegmentTooltip(column: string, value: string): string {
        const key = (value ?? '').normalize('NFC').trim();
        let meta: SegmentMetadataItem | undefined;
        if (column === 'segment_achats') {
            meta = this.achatMetadataMap[key];
        } else if (column === 'segment_email') {
            meta = this.emailMetadataMap[key];
        }
        if (!meta) return '';
        const parts: string[] = [];
        if (meta.description) parts.push(meta.description);
        if (meta.criteria) parts.push(`Critère: ${meta.criteria}`);
        return parts.join(' — ');
    }

    getAvailableOperators(column: string): string[] {
        if (this.isStringColumn(column)) {
            return ['IN', 'NOT IN'];
        }
        if (this.isDateColumn(column)) {
            return ['=', '>', '<', '>=', '<=', 'between'];
        }
        return ['=', '!=', '>', '<', '>=', '<=', 'between'];
    }

    onFilterColumnChange(filter: any) {
        // Reset value when column changes
        if (this.isStringColumn(filter.column)) {
            filter.value = [];
            filter.operator = 'IN';
        } else {
            filter.value = '';
            filter.operator = 'between';
        }
        filter.valueMin = null;
        filter.valueMax = null;
        this.updateFilter();
    }

    onNumberRangeChange(filter: any) {
        // Combine min/max into the filter value for backend processing
        filter.value = { min: filter.valueMin, max: filter.valueMax };
        this.updateFilter();
    }

    constructor(
        private campaignService: CampaignService,
        private categoryService: CategoryService,
        private fb: FormBuilder,
        private dicoService: DicoService,
        private cd: ChangeDetectorRef,
        private dialogService: NbDialogService,
        private ngZone: NgZone
    ) { }

    // ...

    ngOnInit(): void {
        console.log('CampaignSidebarComponent initialized');
        // initForms removed, handled per group
        this.metaCategories = this.categoryService.metaCategories;
        this.loadCategories();
        this.loadColumns();

        this.calcStatsSubject.pipe(
            debounceTime(300),
            takeUntil(this.destroy$)
        ).subscribe(params => {
            this.ngZone.run(() => {
                this._performCalcGlobalStats(params.fromSlider, params.userModified);
            });
        });

        this.campaignService.getTotalMails().subscribe(count => {
            this.totalMails = count;
            console.log('Total mails loaded:', this.totalMails);
        });

        this.loadSegmentMetadata();
    }

    // ...

    editSegmentFromGroup(group: AudienceGroup, index: number) {
        const segment = group.segments[index];
        if (!segment) return;

        // Restore State for that group's input
        const catInfo = segment.categories[0];
        if (catInfo) {
            if (segment.meta_category) {
                group.metaControl.setValue(segment.meta_category);
            }
            if (catInfo.category) {
                group.searchControl.setValue(catInfo.category.label);
                // We don't have a "currentCategory" state on group, just the input.
                // If we want to fully support "Edit", we might need to select it.
                // But for now, just populating search is enough or we re-trigger add logic?
            }
            // Percentage reuse?
            // If we edit, we usually pop it out of the list and put it back in the form.
        }

        group.segments.splice(index, 1);
        this.markDirty();
        this.calcGlobalStats();
    }

    removeSegmentFromGroup(group: AudienceGroup, index: number) {
        group.segments.splice(index, 1);
        this.filterGroupCategories(group, group.searchControl.value || '');
        this.markDirty();
        this.calcGlobalStats();
    }

    onCategorySelected(group: AudienceGroup, categoryLabel: string) {
        console.log('Category selected:', categoryLabel, 'Group:', group.id);
        if (categoryLabel) {
            this.setCategory(group, categoryLabel);
        }
    }

    setCategory(group: AudienceGroup, label: string) {
        // Find category object in the group's current filtered list or all categories
        const cat = this.allCategories.find(c => c.label === label);
        console.log('setCategory found:', cat);

        if (cat) {
            const metaId = Number(group.metaControl.value);

            const effectivePct = this.globalPressureActive ? this.globalPercentage : this.percentage;
            const newSegment: CampaignSegment = {
                checked: true,
                meta_category: metaId,
                categories: [{
                    category: cat,
                    percentage: effectivePct,
                    potential: 0,
                    mailCount: 0,
                    lift: 0
                }],
                percentage: effectivePct,
                mailList: []
            };

            group.segments.push(newSegment);
            this.markDirty();

            // Reset Input — use setTimeout to refresh AFTER Nebular writes the
            // selected label back into the input (same pattern as CRM filter tags)
            group.searchControl.setValue('', { emitEvent: false });
            this.filterGroupCategories(group, '');
            setTimeout(() => {
                group.searchControl.setValue('', { emitEvent: false });
                this.filterGroupCategories(group, '');
                this.cd.detectChanges();
            });

            this.calcGlobalStats();
        }
    }

    loadCategories() {
        this.categoryService.getPlanningCategories().subscribe({
            next: (data) => {
                this.metaCategories = data.metas;
                this.planningCategoriesMap = data.cats;
                console.log('Meta categories loaded:', this.metaCategories);

                this.allCategories = [];
                Object.values(this.planningCategoriesMap).forEach(catList => {
                    this.allCategories.push(...catList);
                });

                this.initCampaign();
                this.cd.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load categories', err);
                this.initCampaign();
                this.cd.detectChanges();
            }
        });
    }

    loadColumns() {
        this.campaignService.getColumns().subscribe({
            next: (cols) => {
                this.columnDefs = cols;
                // Detect date column for "exclude recent buyers" checkbox
                // Try by type first, then by name pattern (must contain "date" + "purchase"/"achat", or "last_purchase")
                this.dateColumn = Object.keys(cols).find(k => cols[k].type === 'date')
                    || Object.keys(cols).find(k => {
                        const lk = k.toLowerCase();
                        return lk.includes('last_purchase') || (lk.includes('date') && (lk.includes('purchase') || lk.includes('achat')));
                    })
                    || null;
                // Exclude the date column from filters — the checkbox handles it
                // Exclure buyer_channel : controle par le selecteur global, pas par les filtres CRM
                this.availableColumns = Object.keys(cols).filter(k => k !== this.dateColumn && k !== 'buyer_channel');
            },
            error: (err) => console.error('Error loading columns', err)
        });
    }

    loadSegmentMetadata() {
        this.campaignService.getSegmentMetadata(this.selectedChannel).subscribe({
            next: (meta) => {
                this.segmentMetadata = meta;
                // Build color maps and lookup maps from metadata
                if (meta['achats']) {
                    this.achatMetadataMap = {};
                    meta['achats'].forEach(item => {
                        this.achatMetadataMap[item.name] = item;
                        if (item.color) {
                            this.achatColors[item.name] = item.color;
                        }
                    });
                }
                if (meta['email']) {
                    this.emailMetadataMap = {};
                    meta['email'].forEach(item => {
                        this.emailMetadataMap[item.name] = item;
                        if (item.color) {
                            this.emailColors[item.name] = item.color;
                        }
                    });
                }
                if (meta['valeur']) {
                    this.valeurMetadataMap = {};
                    meta['valeur'].forEach(item => {
                        this.valeurMetadataMap[item.name] = item;
                    });
                }
                this.cd.detectChanges();
            },
            error: (err) => console.error('Error loading segment metadata', err)
        });
    }

    initCampaign() {
        if (!this.campaignInput) {
            this.campaign = {
                date: `${this.date.getFullYear()}-${String(this.date.getMonth() + 1).padStart(2, '0')}-${String(this.date.getDate()).padStart(2, '0')}`,
                name: '',
                segments: [],
                status: 'DRAFT'
            };
            this.addGroup();
        } else {
            this.campaign = { ...this.campaignInput };

            // Handle new format: { audiences: [[...], [...]], stats: {...}, filters: [...], pressure: N }
            const seg: any = this.campaign.segments;
            let audiencesData: any[] = [];
            if (seg && !Array.isArray(seg) && seg.audiences) {
                audiencesData = seg.audiences;
                // Pre-populate stats so the card shows while the API call runs
                if (seg.stats) {
                    this.globalStats = { ...seg.stats, pourcentBase: 0, percentage: 0 };
                }
                // Restore filters. Le filtre 'channel' (meta='channel') n'est
                // pas user-editable : on l'extrait pour selectedChannel puis on
                // le retire des filters visibles. Compatible avec l'ancien
                // format (value=['mixte'] ou ['web']) via fallback _channel.
                if (Array.isArray(seg.filters)) {
                    const channelFilter = seg.filters.find((f: any) => f?._meta === 'channel');
                    if (channelFilter) {
                        if (channelFilter._channel === 'web' || channelFilter._channel === 'magasin') {
                            this.selectedChannel = channelFilter._channel;
                        } else if (Array.isArray(channelFilter.value)) {
                            // Fallback : deduit depuis la valeur (ancien format)
                            const vals = new Set(channelFilter.value);
                            if (vals.has('web')) this.selectedChannel = 'web';
                            else if (vals.has('magasin')) this.selectedChannel = 'magasin';
                        }
                    }
                    this.filters = seg.filters.filter((f: any) => f?._meta !== 'channel');
                }
                // Restore exclude recent buyers
                if (seg.excludeRecentBuyers) {
                    this.excludeRecentBuyers = true;
                }
                // Restore channel filter (web / magasin / mixte / all)
                if (typeof seg.channel === 'string') {
                    this.selectedChannel = seg.channel;
                }
                // Restore pressure slider
                if (seg.pressure != null && seg.pressure > 0) {
                    this.globalPercentage = seg.pressure;
                    this.globalPressureActive = true;
                    this.savedPressure = seg.pressure;
                }
                // Preserve capping from optimize-dialog — re-injected transparently on save
                if (seg.capping) {
                    this.existingCapping = seg.capping;
                }
            } else {
                audiencesData = Array.isArray(seg) ? seg : [];
            }

            if (audiencesData.length > 0) {
                if (Array.isArray(audiencesData[0])) {
                    audiencesData.forEach((groupParams: any[]) => {
                        const segments = this.mapJsonToSegments(groupParams);
                        this.audienceGroups.push(this.createAudienceGroup(segments));
                    });
                } else {
                    const segments = this.mapJsonToSegments(audiencesData);
                    this.audienceGroups.push(this.createAudienceGroup(segments));
                }
            } else {
                this.addGroup();
            }
        }
        this.calcGlobalStats();
    }

    createAudienceGroup(segments: CampaignSegment[] = []): AudienceGroup {
        const group: AudienceGroup = {
            id: Date.now() + Math.random(),
            segments: segments,
            searchControl: new FormControl(''),
            metaControl: new FormControl(null),
            filteredCategories$: of([])
        };

        group.metaControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(metaId => {
            this.updateGroupCategories(group, metaId);
            this.markDirty();
            this.calcGlobalStats();
        });

        group.searchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(term => {
            this.filterGroupCategories(group, term);
        });

        this.filterGroupCategories(group, '');

        if (segments.length > 0 && segments[0].meta_category) {
            let mId = segments[0].meta_category;
            if (Array.isArray(mId)) mId = mId[0]; // handle array case
            group.metaControl.setValue(mId, { emitEvent: false });
            this.updateGroupCategories(group, mId);
        } else if (this.metaCategories.length > 0) {
            group.metaControl.setValue(this.metaCategories[0].id, { emitEvent: false });
            this.updateGroupCategories(group, this.metaCategories[0].id);
        }

        return group;
    }

    updateGroupCategories(group: AudienceGroup, metaId: number | number[]) {
        const id = Array.isArray(metaId) ? metaId[0] : metaId;
        if (id) {
            const cats = this.planningCategoriesMap[Number(id)] || [];
            group.filteredCategories$ = of(this.filterCats(cats, group.searchControl.value, group));
        }
    }

    filterGroupCategories(group: AudienceGroup, term: string) {
        const metaId = group.metaControl.value;
        if (!metaId) return;
        const id = Array.isArray(metaId) ? metaId[0] : metaId;
        const cats = this.planningCategoriesMap[Number(id)] || [];
        group.filteredCategories$ = of(this.filterCats(cats, term, group));
    }

    private filterCats(cats: Category[], term: string, group?: AudienceGroup): Category[] {
        // Exclude categories already added to this group (like CRM filters do)
        let filtered = cats;
        if (group) {
            const selectedIds = new Set(group.segments.map(s => s.categories[0]?.category?.id).filter(id => id != null));
            filtered = filtered.filter(c => !selectedIds.has(c.id));
        }
        if (!term) return filtered;
        return filtered.filter(c => c.label.toLowerCase().includes(term.toLowerCase()));
    }

    addGroup() {
        const group = this.createAudienceGroup();
        this.audienceGroups.push(group);
        // No API call here — the new group has no segments yet
    }

    removeGroup(index: number) {
        if (this.audienceGroups.length <= 1 && this.audienceGroups[index].segments.length === 0) return;
        this.audienceGroups.splice(index, 1);
        if (this.audienceGroups.length === 0) {
            this.addGroup();
        }
        this.markDirty();
        this.calcGlobalStats();
    }

    addFilter() {
        const col = this.availableColumns[0];
        const isNum = !this.isStringColumn(col);
        this.filters.push({
            column: col,
            operator: isNum ? 'between' : 'IN',
            value: isNum ? '' : []
        });
        this.updateFilter();
    }

    removeFilter(index: number) {
        this.filters.splice(index, 1);
        this.updateFilter();
    }

    onExcludeRecentBuyersChange() {
        this.markDirty();
        this.calcGlobalStats();
    }

    onChannelChange() {
        this.markDirty();
        // Recharge la metadata avec le bon channel (la matrice se recharge via ngOnChanges)
        this.loadSegmentMetadata();
        // Recalcule volumes + demographiques avec le filtre channel
        this.calcGlobalStats();
    }

    updateFilter() {
        this.markDirty();
        this.calcGlobalStats();
    }

    markDirty() {
        this.hasUnsavedChanges = true;
    }

    save() {
        if (!this.campaign) return;
        if (!this.campaign.name?.trim()) {
            this.errorMessage = 'Veuillez renseigner un nom de campagne.';
            this.dialogService.open(this.dialogCurveError!);
            return;
        }
        const nestedSegments = this.audienceGroups.map(g => {
            return this.mapSegmentsToJson(g.segments);
        });

        // Persistence : on prepend le filtre channel (invisible cote UI) dans
        // segments.filters pour qu'il s'applique automatiquement au dispatch
        // (Celery lit segments.filters), sans modif backend.
        // Semantique inclusive : 'web' couvre web + mixte (idem magasin).
        let channelFilter: any = null;
        if (this.selectedChannel === 'web') {
            channelFilter = { column: 'buyer_channel', operator: 'IN', value: ['web', 'mixte'], _meta: 'channel', _channel: 'web' };
        } else if (this.selectedChannel === 'magasin') {
            channelFilter = { column: 'buyer_channel', operator: 'IN', value: ['magasin', 'mixte'], _meta: 'channel', _channel: 'magasin' };
        }
        const persistedFilters: any[] = channelFilter
            ? [channelFilter, ...this.filters]
            : [...this.filters];

        // Wrap audiences + stats + filters + pressure so everything is restored on reopen
        const payload: any = {
            ...this.campaign,
            segments: {
                audiences: nestedSegments,
                stats: this.globalStats ? {
                    numb_mail: this.globalStats.numb_mail,
                    lift: this.globalStats.lift,
                    potential: this.globalStats.potential
                } : null,
                filters: persistedFilters,
                pressure: this.globalPercentage,
                excludeRecentBuyers: this.excludeRecentBuyers,
                channel: this.selectedChannel,
                ...(this.existingCapping ? { capping: { ...this.existingCapping, stale: true } } : {})
            }
        };

        if (payload.id) {
            this.campaignService.updateScheduledCampaign(payload.id, payload).subscribe((result) => {
                this.hasUnsavedChanges = false;
                this.saved.emit(result || payload);
                this.closed.emit();
            });
        } else {
            this.campaignService.createScheduledCampaign(payload).subscribe((result) => {
                this.hasUnsavedChanges = false;
                this.saved.emit(result || payload);
                this.closed.emit();
            });
        }
    }

    onGlobalSliderInput(changeContext: any) {
        this.markDirty();
        this.globalPressureActive = true;
        this.globalPercentage = changeContext.value;
        this.updateGlobalStatsLocally(this.globalPercentage);
        // Update curve: backend optimal stays fixed, slider position shown as secondary dot
        if (this.storedRawCurve) {
            this.updateGlobalCurve(this.storedRawCurve, this.backendOptimal, this.globalPercentage);
        }
        this.cd.detectChanges();
    }

    onGlobalSliderChangeEnd(changeContext: any) {
        this.calcGlobalStats(true, true);
    }

    calcGlobalStats(fromSlider: boolean = false, userModified: boolean = false) {
        this.calcStatsSubject.next({ fromSlider, userModified });
    }

    _doCalcGlobalStats(fromSlider: boolean = false, userModified: boolean = false) {
        if (!this.audienceGroups) return;

        const allSegments = this.audienceGroups.flatMap(g => g.segments);
        this.hasAudiences = allSegments.length > 0;


        // When the global slider is active (user explicitly set it or restored from save),
        // override pctBase. Otherwise, let the backend auto-compute from optimal.
        const globalPct = this.globalPressureActive ? this.globalPercentage : null;
        // Always ask backend for optimal so the marker updates when filters change
        const autoPressure = true;
        const audiences = this.audienceGroups.map(g => {
            const segs = this.mapSegmentsToJson(g.segments);
            return globalPct !== null ? segs.map((aud: any) => ({ ...aud, pctBase: globalPct })) : segs;
        });
        const capping = 100;

        this.isGlobalLoading = true;
        this.cd.detectChanges();

        // Cancel any in-flight request to prevent stale responses from overwriting newer results
        this.globalPreviewSubscription?.unsubscribe();
        // Build effective filters: add exclude recent buyers if checked
        const effectiveFilters = [...this.filters];
        if (this.excludeRecentBuyers && this.dateColumn) {
            const today = new Date();
            const cutoff = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
            effectiveFilters.push({
                column: this.dateColumn,
                operator: 'between',
                value: { min: cutoff.toISOString().slice(0, 10), max: today.toISOString().slice(0, 10) },
                exclude: true
            });
        }
        // Filtre global canal d'achat (semantique inclusive : web = web + mixte,
        // magasin = magasin + mixte). 'all' = pas de filtre.
        if (this.selectedChannel === 'web') {
            effectiveFilters.push({ column: 'buyer_channel', operator: 'IN', value: ['web', 'mixte'] });
        } else if (this.selectedChannel === 'magasin') {
            effectiveFilters.push({ column: 'buyer_channel', operator: 'IN', value: ['magasin', 'mixte'] });
        }
        this.globalPreviewSubscription = this.campaignService.previewPlanning(audiences, effectiveFilters, capping, true, autoPressure).subscribe({
            next: (res) => this.ngZone.run(() => {
                this.isGlobalLoading = false;

                // Use the sweet spot (optimal) from the backend to position the slider
                // unless the user has manually adjusted it or pressure was restored from save
                const optimal: number = res.segments?.[0]?.optimal || 0;
                if (optimal > 0) {
                    this.backendOptimal = optimal;
                }
                if (this.savedPressure !== null) {
                    // First load: restore saved pressure, then clear the flag
                    this.globalPercentage = this.savedPressure;
                    this.globalPressureActive = true;
                    this.savedPressure = null;
                } else if (!this.globalPressureActive && optimal > 0) {
                    // No manual slider yet → auto-position on optimal
                    this.globalPercentage = optimal;
                }

                this.isGlobalLoading = false;

                this.globalStats = {
                    numb_mail: res.numb_all_mail,
                    pourcentBase: 0,
                    potential: 0,
                    lift: 0,
                    percentage: capping
                };

                // Update filtered base count BEFORE computing stats so volume uses the right base
                if ((res as any).scope_count !== undefined) {
                    this.globalScopeCount = (res as any).scope_count;
                }

                if (res.global_curve) {
                    this.updateGlobalCurve(res.global_curve, this.backendOptimal, this.globalPercentage);
                    this.updateGlobalStatsLocally(this.globalPercentage);
                }

                if (res.infos_crm) {
                    this.processInfosCRMData(res.infos_crm);
                }

                this.cd.detectChanges();
            }),
            error: (err) => this.ngZone.run(() => {
                this.isGlobalLoading = false;
                this.cd.detectChanges();
                console.error(err);
            })
        });
    }

    _performCalcGlobalStats(fromSlider: boolean, userModified: boolean) {
        this._doCalcGlobalStats(fromSlider, userModified);
    }

    updateGlobalStatsLocally(percentageOverride?: number) {
        if (this.globalCurveData && this.storedRawCurve) {
            const pct = percentageOverride ?? this.globalPercentage;
            // Find exact point or fallback to the closest available point on the curve
            const point = this.storedRawCurve.find(p => p.x === pct)
                ?? this.storedRawCurve.reduce((prev, curr) =>
                    Math.abs(curr.x - pct) < Math.abs(prev.x - pct) ? curr : prev
                );
            if (point && this.globalStats) {
                const base = this.globalScopeCount > 0 ? this.globalScopeCount : this.totalMails;
                this.globalStats.numb_mail = Math.round(base * (pct / 100));
                this.globalStats.lift = point.y;
                this.globalStats.potential = Math.round(pct * point.y);
                this.globalStats.pourcentBase = pct;
            }
        }
    }

    updateGlobalCurve(curveData: { x: number, y: number }[], optimalPct?: number, sliderPct?: number) {
        this.storedRawCurve = curveData;

        // Update X-axis label based on active filters
        const xLabel = this.filters.some(f => Array.isArray(f.value) ? f.value.length > 0 : !!f.value)
            ? '% Base Filtrée' : '% Base Totale';
        const currentScales = this.globalCurveOptions?.scales || {};
        const currentX = (currentScales as any)['x'] || {};
        this.globalCurveOptions = {
            ...this.globalCurveOptions,
            scales: {
                ...currentScales,
                x: { ...currentX, title: { display: true, text: xLabel } }
            }
        };

        const findClosest = (pts: { x: number, y: number }[], target: number) =>
            pts.find(p => p.x === target)
            ?? pts.reduce((prev, curr) => Math.abs(curr.x - target) < Math.abs(prev.x - target) ? curr : prev);

        const datasets: any[] = [
            {
                data: curveData,
                label: 'Lift',
                borderColor: '#35a2eb',
                backgroundColor: 'rgba(53, 162, 235, 0.3)',
                fill: 'origin',
                tension: 0,
                pointRadius: 0
            }
        ];

        // Backend optimal — always visible as semi-transparent dot
        if (optimalPct && optimalPct > 0) {
            const optimalPoint = findClosest(curveData, optimalPct);
            if (optimalPoint) {
                datasets.push({
                    data: [optimalPoint],
                    label: 'Optimal',
                    borderColor: 'rgba(255, 99, 132, 0.5)',
                    backgroundColor: 'rgba(255, 99, 132, 0.5)',
                    pointRadius: 8,
                    pointHoverRadius: 10,
                    type: 'scatter',
                    showLine: false
                });
            }
        }

        // Current slider position — solid dot, always on top (rendered last)
        if (sliderPct && sliderPct > 0) {
            const sliderPoint = findClosest(curveData, sliderPct);
            if (sliderPoint) {
                datasets.push({
                    data: [sliderPoint],
                    label: 'Sélection',
                    borderColor: '#ff6384',
                    backgroundColor: '#ff6384',
                    pointRadius: 8,
                    pointHoverRadius: 10,
                    type: 'scatter',
                    showLine: false
                });
            }
        }

        this.globalCurveData = { datasets };
    }

    mapSegmentsToJson(segments: CampaignSegment[]): any[] {
        const flatAudiences: any[] = [];
        segments.forEach(s => {
            s.categories.forEach(c => {
                flatAudiences.push({
                    meta_category: s.meta_category,
                    category: c.category.id,
                    pctBase: c.percentage
                });
            });
        });
        return flatAudiences;
    }

    ngOnDestroy(): void {
        console.log('CampaignSidebarComponent destroyed');
        this.destroy$.next();
        this.destroy$.complete();
        this.globalPreviewSubscription?.unsubscribe();
    }

    ngOnChanges(changes: SimpleChanges): void { }

    deleteCampaign() {
        if (!this.campaign || !this.campaign.id) return;
        this.dialogService.open(this.deleteConfirmDialog).onClose.subscribe((confirmed: boolean) => {
            if (confirmed) {
                this.campaignService.deleteScheduledCampaign(this.campaign!.id!).subscribe(() => this.closed.emit());
            }
        });
    }

    mapJsonToSegments(json: any[]): CampaignSegment[] {
        if (!json || !Array.isArray(json)) return [];
        return json.map(j => {
            if (j.category !== undefined) {
                let catObj = this.allCategories.find(c => c.id === j.category && c.meta_category === j.meta_category)
                    || this.allCategories.find(c => c.id === j.category);
                const segCat = {
                    category: catObj || { id: j.category, label: 'Inconnu' } as Category,
                    percentage: j.percentage || 30,
                    potential: 0, mailCount: 0, lift: 0
                };
                return {
                    checked: true,
                    meta_category: j.meta_category,
                    categories: [segCat],
                    percentage: j.percentage || 30,
                    mailList: []
                } as CampaignSegment;
            }
            return {} as CampaignSegment;
        }).filter(x => x.categories);
    }

    processInfosCRMData(parsedData: any) {
        if (!parsedData) return;

        if (parsedData.segment_achats) {
            const labelsAchat = parsedData.segment_achats.map((item: any) => item.segment_achats);
            const valuesAchat = parsedData.segment_achats.map((item: any) => item.count_size);
            this.pieChartDataSegmentAchat = {
                labels: labelsAchat,
                datasets: [{
                    data: valuesAchat,
                    backgroundColor: colorsFor(labelsAchat, this.achatColors, DEFAULT_ACHAT_COLORS),
                    hoverOffset: 4
                }],
            };
            this.pieChartOptionsSegmentAchat = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 }, padding: 6 } },
                    tooltip: this.buildPieTooltipOptions(() => this.achatMetadataMap)
                }
            };
        }

        if (parsedData.segment_email) {
            const labelsEmail = parsedData.segment_email.map((item: any) => item.segment_email);
            const valuesEmail = parsedData.segment_email.map((item: any) => item.count_size);
            this.pieChartDataSegmentEmail = {
                labels: labelsEmail,
                datasets: [{
                    data: valuesEmail,
                    backgroundColor: colorsFor(labelsEmail, this.emailColors, DEFAULT_EMAIL_COLORS),
                    hoverOffset: 4
                }],
            };
            this.pieChartOptionsSegmentEmail = {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: { position: 'right', labels: { boxWidth: 10, font: { size: 11 }, padding: 6 } },
                    tooltip: this.buildPieTooltipOptions(() => this.emailMetadataMap)
                }
            };
        }

        if (parsedData.age_pyramid?.length) {
            this.processAgePyramidData(parsedData.age_pyramid);
        }

        if (parsedData.realite_genre?.length) {
            this.processRealiteGenreData(parsedData.realite_genre);
        }

        if (parsedData.segment_genre?.length) {
            this.processSegmentGenreData(parsedData.segment_genre);
        }

        if (parsedData.map) {
            this.regionsMapData = parsedData.map;
            // Ensure Angular updates the DOM (display:block) before D3 tries to select the container
            this.cd.detectChanges();
            setTimeout(() => this.initializeRegionsMap(), 0);
        }
    }
    processAgePyramidData(agePyramid: any[]) {
        const sortedAgePyramid = [...agePyramid].sort((a, b) => {
            return parseInt(a.age_group.split('-')[0]) - parseInt(b.age_group.split('-')[0]);
        });
        const ageGroups = sortedAgePyramid.map(item => item.age_group);
        const femaleData = sortedAgePyramid.map(item => -item.MME);
        const maleData = sortedAgePyramid.map(item => item.MR);

        const maxValue = Math.max(...femaleData.map(Math.abs), ...maleData);

        this.agePyramidOptions = {
            ...this.agePyramidOptions,
            scales: {
                ...this.agePyramidOptions.scales,
                x: {
                    ...this.agePyramidOptions.scales?.['x'],
                    min: -maxValue,
                    max: maxValue
                }
            }
        };

        this.agePyramidData = {
            labels: ageGroups,
            datasets: [
                { label: 'Femme', data: femaleData, backgroundColor: 'rgba(255, 99, 132, 0.7)' },
                { label: 'Homme', data: maleData, backgroundColor: 'rgba(54, 162, 235, 0.7)' }
            ]
        };
    }
    processRealiteGenreData(realiteGenre: any[]) {
        const dataToRender = (realiteGenre || []).sort((a: any, b: any) => a.civilite === 'MME' ? -1 : 1);
        this.genreRealiteChartData = {
            labels: dataToRender.map((item: any) => item.civilite),
            datasets: [{
                data: dataToRender.map((item: any) => item.percentage),
                backgroundColor: dataToRender.map((item: any) =>
                    item.civilite === 'MME' ? 'rgba(255, 99, 132, 0.7)' : 'rgba(54, 162, 235, 0.7)'
                )
            }]
        };
    }

    processSegmentGenreData(segmentGenre: any[]) {
        const dataToRender = segmentGenre || [];
        const backgroundColor = dataToRender.map((item: any) => {
            return item.civilite === 'MME' ? 'rgba(255, 99, 132, 0.7)' : 'rgba(54, 162, 235, 0.7)';
        });

        this.genreSegmentChartData = {
            labels: dataToRender.map((item: any) => item.civilite),
            datasets: [{
                data: dataToRender.map((item: any) => item.percentage),
                backgroundColor: backgroundColor
            }]
        };
    }
    onGenreChartClick(event: any) { }

    initializeRegionsMap(): void {
        d3.select('#sidebar-regions-map-container').selectAll('*').remove();
        d3.select('body').selectAll('.sidebar-regions-map-tooltip').remove();

        if (!this.regionsMapData || this.regionsMapData.length === 0) return;

        this.regionsMapContainer = d3.select('#sidebar-regions-map-container')
            .append('svg')
            .attr('width', this.regionsMapWidth)
            .attr('height', this.regionsMapHeight)
            .attr('viewBox', `0 0 ${this.regionsMapWidth} ${this.regionsMapHeight}`)
            .attr('preserveAspectRatio', 'xMidYMid meet');

        this.regionsMapSvg = this.regionsMapContainer.append('g');

        this.regionsMapTooltip = d3.select('body').append('div')
            .attr('class', 'sidebar-regions-map-tooltip')
            .style('opacity', 0)
            .style('position', 'absolute')
            .style('background-color', 'white')
            .style('border', '1px solid #ddd')
            .style('border-radius', '3px')
            .style('padding', '5px')
            .style('pointer-events', 'none')
            .style('z-index', 1060);

        // Reuse cached GeoJSON if already loaded, otherwise fetch once
        if (this.regionsGeoData) {
            this.updateRegionsMapWithData();
        } else {
            d3.json('assets/regions-version-simplifiee.geojson').then((geoData: any) => {
                this.regionsGeoData = geoData;
                // Re-call initializeRegionsMap so it rebuilds the SVG cleanly with cached data
                this.initializeRegionsMap();
            });
        }
    }

    updateRegionsMapWithData(): void {
        if (!this.regionsGeoData || !this.regionsMapData.length) return;

        this.regionsMapSvg.selectAll('*').remove();
        this.regionsMapContainer.selectAll('.sidebar-regions-map-legend').remove();

        const counts = this.regionsMapData.map(d => d.count);
        const minCount = Math.min(...counts);
        const maxCount = Math.max(...counts);

        this.regionsMapColorScale = d3.scaleLinear<string>()
            .domain([minCount, 0, maxCount])
            .range(['#35a2eb', '#f9f9f9', '#ff6384'])
            .interpolate(d3.interpolateRgb);

        const projection = d3.geoMercator()
            .center([2.2, 46.8])
            .scale(1800)
            .translate([this.regionsMapWidth / 2, this.regionsMapHeight / 2]);

        const path = d3.geoPath().projection(projection);

        this.regionsMapSvg.selectAll('path')
            .data(this.regionsGeoData.features)
            .enter()
            .append('path')
            .attr('d', path)
            .attr('fill', (d: any) => {
                const regionCode = d.properties.code;
                const regionData = this.regionsMapData.find(item => item.code_region.toString() === regionCode);
                return regionData ? this.regionsMapColorScale(regionData.count) : '#f9f9f9';
            })
            .attr('stroke', '#fff')
            .attr('stroke-width', 0.5)
            .on('mouseover', (event: any, d: any) => {
                const regionCode = d.properties.code;
                const regionData = this.regionsMapData.find(item => item.code_region.toString() === regionCode);
                const rawCount = regionData ? regionData.count : 0;
                const formattedCount = rawCount > 0 ? `+${rawCount} %` : `${rawCount} %`;
                this.regionsMapTooltip.transition().duration(200).style('opacity', .9);
                this.regionsMapTooltip.html(`
                    <strong>Region:</strong> ${d.properties.nom}<br/>
                    <strong>Différence vs base totale:</strong> ${formattedCount}
                `)
                    .style('left', (event.pageX + 10) + 'px')
                    .style('top', (event.pageY - 28) + 'px');
            })
            .on('mouseout', () => {
                this.regionsMapTooltip.transition().duration(500).style('opacity', 0);
            });

        this.addRegionsMapLegend(minCount, maxCount);
    }

    addRegionsMapLegend(minValue: number, maxValue: number): void {
        const gradientId = 'sidebar-regions-map-legend-gradient-' + new Date().getTime();

        const legend = this.regionsMapContainer.append('g')
            .attr('class', 'sidebar-regions-map-legend')
            .attr('transform', `translate(${this.regionsMapWidth - 60}, ${(this.regionsMapHeight / 2) - 100})`);

        const defs = this.regionsMapContainer.append('defs');
        const gradient = defs.append('linearGradient')
            .attr('id', gradientId)
            .attr('x1', '0%').attr('y1', '100%')
            .attr('x2', '0%').attr('y2', '0%');

        gradient.append('stop').attr('offset', '0%').attr('stop-color', '#35a2eb');
        gradient.append('stop').attr('offset', '50%').attr('stop-color', '#f9f9f9');
        gradient.append('stop').attr('offset', '100%').attr('stop-color', '#ff6384');

        const legendWidth = 20;
        const legendHeight = 200;

        legend.append('rect')
            .attr('width', legendWidth)
            .attr('height', legendHeight)
            .style('fill', `url(#${gradientId})`);

        legend.append('text')
            .attr('x', legendWidth / 2)
            .attr('y', -15)
            .attr('text-anchor', 'middle')
            .style('font-size', '12px')
            .text('Diff. %');
    }

    onFilterTagAdd(filter: any, event: any, inputEl?: HTMLInputElement) {
        if (!filter.value) filter.value = [];
        if (!Array.isArray(filter.value)) filter.value = [filter.value];

        const val = event.value || event;
        if (typeof val === 'string' && val.trim() !== '' && !filter.value.includes(val)) {
            filter.value.push(val);
        }
        // Nebular writes the selected value into the input — clear it at DOM level
        filter.searchValue = '';
        if (inputEl) inputEl.value = '';
        setTimeout(() => {
            filter.searchValue = '';
            if (inputEl) inputEl.value = '';
            this.cd.detectChanges();
        });
        this.updateFilter();
    }
    onFilterTagRemove(filter: any, index: number) {
        if (Array.isArray(filter.value)) {
            filter.value.splice(index, 1);
            this.updateFilter();
        }
    }
    getFilterOptionsObservable(filter: any): Observable<string[]> {
        const def = this.columnDefs[filter.column];
        if (!def || !def.options) return of([]);
        const selected: string[] = Array.isArray(filter.value) ? filter.value : [];
        const available = def.options.filter((o: string) => !selected.includes(o));
        const term = filter.searchValue || '';
        if (!term) return of(available);
        return of(available.filter((o: string) => o.toLowerCase().includes(term.toLowerCase())));
    }

    getSegmentLabel(segment: CampaignSegment): string {
        if (!segment.categories || segment.categories.length === 0) return 'Inconnu';
        return `${segment.categories[0].category.label}`;
    }

    suggestName() {
        if (!this.campaign || this.isSuggestingName) return;
        this.isSuggestingName = true;
        this.cd.detectChanges();

        // Build rich context: meta_category label + category labels
        const categories: string[] = [];
        for (const g of this.audienceGroups) {
            const metaId = g.metaControl?.value;
            const metaLabel = this.metaCategories?.find(m => m.id === Number(metaId))?.label;
            for (const seg of g.segments) {
                const catLabel = seg.categories?.[0]?.category?.label;
                if (metaLabel && catLabel) {
                    categories.push(`${metaLabel} > ${catLabel}`);
                } else if (catLabel) {
                    categories.push(catLabel);
                }
            }
        }
        const filters = this.filters.filter((f: any) => f.column && (f.value || f.valueMin));

        this.campaignService.suggestName({
            categories,
            filters,
            pressure: this.globalPercentage,
            num_emails: this.globalStats?.numb_mail
        }).subscribe({
            next: (res) => this.ngZone.run(() => {
                this.campaign!.name = res.name;
                this.markDirty();
                this.isSuggestingName = false;
                this.cd.detectChanges();
            }),
            error: () => this.ngZone.run(() => {
                this.isSuggestingName = false;
                this.cd.detectChanges();
            })
        });
    }

    /**
     * Reçoit un événement depuis la matrice de segmentation et ajoute
     * les valeurs aux filtres CRM existants (mode additif).
     */
    onMatrixAddToSegment(event: { segment_email: string[]; axis_type: string; segment_y: string[] }) {
        // Helper: find or create a filter for a given column and add values (additif)
        const addToFilter = (column: string, values: string[]) => {
            let filter = this.filters.find((f: any) => f.column === column && f.operator === 'IN');
            if (!filter) {
                filter = { column, operator: 'IN', value: [] };
                this.filters.push(filter);
            }
            if (!Array.isArray(filter.value)) filter.value = [];
            for (const v of values) {
                if (!filter.value.includes(v)) {
                    filter.value.push(v);
                }
            }
        };

        // Map axis_type → CRM filter column name
        const axisColumnMap: Record<string, string> = {
            'achats': 'segment_achats',
            'fidelite': 'segment_fidelite',
            'valeur': 'segment_valeur',
        };

        if (event.segment_email.length > 0) {
            addToFilter('segment_email', event.segment_email);
        }
        const yColumn = axisColumnMap[event.axis_type] ?? `segment_${event.axis_type}`;
        if (event.segment_y.length > 0) {
            addToFilter(yColumn, event.segment_y);
        }

        this.updateFilter();
    }
}
